/**
 * Brand Navigation is used on the PLP to show a Slick carousel of tiles that are either links or filters
 * and two way data bind with the other Brand facets
 * Additional info on the facets can be found in facets.js
 */

const BrandNavigation = {
  brandNavSlider: null,
  brandNavInputs: null,
  filterTiles: null,
  desktopMediaQuery: null,
  resizeTimer: null,
  isClampingEndpoint: false,

  /**
   * Initializes the brand navigation module
   * Sets up DOM elements, slider, event listeners, and initial selection
   */
  init() {
    this.cacheDOMElements();
    this.setupSlider();
    this.setupEventListeners();
    this.addSelectedClassOnLoad();
  },

  /**
   * Caches DOM elements for performance and reusability
   */
  cacheDOMElements() {
    this.brandNavSlider = document.querySelector(
      '.all-brands-filters-container'
    );
    this.brandNavInputs = document.querySelectorAll('.filter-checkbox');
    this.filterTiles = document.querySelectorAll('label.js-filter-tile');
  },

  /**
   * Updates a tile and its close button to match the selected state
   * @param {HTMLElement} tile - The lifestyle filter tile
   * @param {boolean} isSelected - Whether the filter is selected
   */
  setTileSelectedState(tile, isSelected) {
    if (!tile) return;

    tile.classList.toggle('selected', isSelected);

    const closeButton = tile.querySelector('.close');
    if (closeButton) {
      closeButton.setAttribute('aria-hidden', String(!isSelected));
      closeButton.setAttribute('tabindex', isSelected ? '0' : '-1');
    }
  },

  /**
   * Sets up the Slick slider with responsive settings and custom navigation
   */
  setupSlider() {
    if (!this.brandNavSlider) return;

    this.desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
    this.handleSliderModeChange = this.handleSliderModeChange.bind(this);
    this.updateDesktopSlidesToShow = this.updateDesktopSlidesToShow.bind(this);

    if (this.desktopMediaQuery.addEventListener) {
      this.desktopMediaQuery.addEventListener(
        'change',
        this.handleSliderModeChange
      );
    } else {
      this.desktopMediaQuery.addListener(this.handleSliderModeChange);
    }

    window.addEventListener('resize', () => {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(this.updateDesktopSlidesToShow, 100);
    });

    this.handleSliderModeChange();
  },

  /**
   * Uses native horizontal scrolling below desktop and Slick where arrow
   * navigation is displayed.
   */
  handleSliderModeChange() {
    const $slider = $(this.brandNavSlider);

    if (this.desktopMediaQuery.matches) {
      this.brandNavSlider.classList.remove('is-native-scroll');

      if (!this.isSlickInitialized()) {
        this.initializeDesktopSlider();
      }
    } else {
      if (this.isSlickInitialized()) {
        $slider.slick('unslick');
      }

      $slider.off('.brandNavigation');
      this.restoreNativeAccessibility();
      this.brandNavSlider.classList.add('is-native-scroll');
    }

    this.goToFirstSelectedSlide();
  },

  /**
   * Initializes Slick for desktop navigation.
   */
  initializeDesktopSlider() {
    const $slider = $(this.brandNavSlider);

    $slider
      .off('.brandNavigation')
      .on(
        'init.brandNavigation reInit.brandNavigation afterChange.brandNavigation',
        () => this.enforceSlickAccessibility()
      )
      .on(
        'beforeChange.brandNavigation',
        (event, slick, currentSlide, nextSlide) =>
          this.clampToEndpoint(nextSlide)
      );

    $slider.slick({
      infinite: false,
      dots: false,
      variableWidth: true,
      swipeToSlide: true,
      slidesToShow: this.getVisibleSlideCount(),
      slidesToScroll: 1,
      prevArrow:
        '<button type="button" tabindex="0" name="previous" aria-label="previous" class="slick-prev"><svg viewBox="0 0 17 11" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" role="img" aria-hidden="true"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-896.000000, -3249.000000)" fill="#FFF" fill-rule="nonzero"><g transform="translate(0.000000, 3096.000000)"><g transform="translate(510.000000, 128.000000)"><path d="M397.654597,25.6421962 C397.436623,25.4416723 397.073841,25.4416723 396.848246,25.6421962 C396.630272,25.8359456 396.630272,26.1584097 396.848246,26.3517075 L400.936909,29.9859772 L387.444879,29.9859772 C387.130367,29.9864289 386.880383,30.208631 386.880383,30.4881902 C386.880383,30.7677494 387.130367,30.9971777 387.444879,30.9971777 L400.936909,30.9971777 L396.848246,34.6246729 C396.630272,34.8251968 396.630272,35.1481126 396.848246,35.3414104 C397.073841,35.5419343 397.437131,35.5419343 397.654597,35.3414104 L402.711186,30.8467848 C402.936782,30.6530353 402.936782,30.3305712 402.711186,30.1372734 L397.654597,25.6421962 Z"></path></g></g></g></g></svg></button>',
      nextArrow:
        '<button type="button" tabindex="0" name="next" aria-label="next" class="slick-next"><svg viewBox="0 0 17 11" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" role="img" aria-hidden="true"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-896.000000, -3249.000000)" fill="#FFF" fill-rule="nonzero"><g transform="translate(0.000000, 3096.000000)"><g transform="translate(510.000000, 128.000000)"><path d="M397.654597,25.6421962 C397.436623,25.4416723 397.073841,25.4416723 396.848246,25.6421962 C396.630272,25.8359456 396.630272,26.1584097 396.848246,26.3517075 L400.936909,29.9859772 L387.444879,29.9859772 C387.130367,29.9864289 386.880383,30.208631 386.880383,30.4881902 C386.880383,30.7677494 387.130367,30.9971777 387.444879,30.9971777 L400.936909,30.9971777 L396.848246,34.6246729 C396.630272,34.8251968 396.630272,35.1481126 396.848246,35.3414104 C397.073841,35.5419343 397.437131,35.5419343 397.654597,35.3414104 L402.711186,30.8467848 C402.936782,30.6530353 402.936782,30.3305712 402.711186,30.1372734 L397.654597,25.6421962 Z"></path></g></g></g></g></svg></button>'
    });

    this.updateDesktopSlidesToShow();
    this.enforceSlickAccessibility();
  },

  /**
   * Whether Slick is currently active on the slider.
   * @returns {boolean}
   */
  isSlickInitialized() {
    return this.brandNavSlider.classList.contains('slick-initialized');
  },

  /**
   * Pulls a pending slide change back to the flush endpoint when it would
   * scroll past the end of the tile content.
   * @param {number} nextSlide - the index Slick is about to move to
   */
  clampToEndpoint(nextSlide) {
    if (this.isClampingEndpoint) return;

    const lastStartingSlide = this.getLastStartingSlide();

    if (nextSlide <= lastStartingSlide) return;

    // slickGoTo re-enters beforeChange, so guard against recursing.
    this.isClampingEndpoint = true;
    $(this.brandNavSlider).slick('slickGoTo', lastStartingSlide);
    this.isClampingEndpoint = false;
  },

  /**
   * Measures an element's width including its horizontal margins.
   * Tiles rely on a right margin for the inter-tile gap, so plain offsetWidth
   * under-reports the space a slide actually occupies.
   * @param {HTMLElement} element
   * @returns {number}
   */
  getOuterWidth(element) {
    const styles = window.getComputedStyle(element);

    return (
      element.offsetWidth +
      parseFloat(styles.marginLeft) +
      parseFloat(styles.marginRight)
    );
  },

  /**
   * Finds the last slide index that can sit at the left edge while the
   * remaining tiles still fill the viewport.
   *
   * variableWidth mode returns a raw offsetLeft with no end clamp, so without
   * this the final position pins the last slide to the left and leaves a
   * mostly-empty container. Walks slides from the end until they overflow the
   * visible width.
   * @returns {number}
   */
  getLastStartingSlide() {
    const slickList = this.brandNavSlider.querySelector('.slick-list');
    const slides = this.brandNavSlider.querySelectorAll('.slick-slide');

    if (!slickList || !slides.length) return 0;

    const visibleWidth = slickList.clientWidth;
    let accumulatedWidth = 0;

    for (let index = slides.length - 1; index >= 0; index -= 1) {
      accumulatedWidth += this.getOuterWidth(slides[index]);

      if (accumulatedWidth > visibleWidth) {
        return index + 1;
      }
    }

    return 0;
  },

  /**
   * Calculates how many complete fixed-width tiles fit in the carousel.
   * Slick uses this value to keep its finite-scroll boundary near the end of
   * the actual tile content.
   * @returns {number}
   */
  getVisibleSlideCount() {
    const sliderStyles = window.getComputedStyle(this.brandNavSlider);
    const slideWidth = parseFloat(
      sliderStyles.getPropertyValue('--brand-navigation-slide-width')
    );
    const slideGap = parseFloat(
      sliderStyles.getPropertyValue('--brand-navigation-slide-gap')
    );
    // Read from CSS rather than restating it. A hardcoded copy of this value
    // drifting from the stylesheet is what hid the nav arrows.
    const endPadding =
      parseFloat(
        sliderStyles.getPropertyValue('--brand-navigation-end-padding')
      ) || 8;

    const sliderPadding =
      parseFloat(sliderStyles.paddingLeft) +
      parseFloat(sliderStyles.paddingRight);
    const sliderContentWidth = this.brandNavSlider.clientWidth - sliderPadding;
    const availableWidth = sliderContentWidth - endPadding;

    const fittedSlideCount = Math.floor(
      (availableWidth + slideGap) / (slideWidth + slideGap)
    );

    // Slick skips updateArrows() entirely when slideCount <= slidesToShow,
    // which strands both arrows disabled. Never let the two meet.
    const maxVisible = Math.max(
      1,
      this.brandNavSlider.querySelectorAll('.filter-tile').length - 1
    );

    return Math.min(maxVisible, Math.max(1, fittedSlideCount));
  },

  /**
   * Keeps Slick's finite-scroll boundary accurate as the desktop layout
   * changes width.
   */
  updateDesktopSlidesToShow() {
    if (!this.isSlickInitialized()) return;

    const $slider = $(this.brandNavSlider);
    const slick = $slider.slick('getSlick');
    const slidesToShow = this.getVisibleSlideCount();

    if (slick.options.slidesToShow !== slidesToShow) {
      $slider.slick('slickSetOption', 'slidesToShow', slidesToShow, true);
    }
  },

  /**
   * Restores the intended tab order after Slick updates slide visibility.
   */
  enforceSlickAccessibility() {
    this.brandNavSlider.querySelectorAll('.filter-checkbox').forEach(cb => {
      cb.setAttribute('tabindex', '-1');
    });

    // Slick's accessibility:true default adds tabindex="0" to .slick-list,
    // creating a ghost tab stop between slides. Remove it.
    const slickList = this.brandNavSlider.querySelector('.slick-list');
    if (slickList) slickList.removeAttribute('tabindex');
  },

  /**
   * Removes tabindex values that Slick adds to links in inactive slides before
   * returning the tiles to the native horizontal scroller.
   */
  restoreNativeAccessibility() {
    this.brandNavSlider
      .querySelectorAll('a.filter-tile[tabindex="-1"]')
      .forEach(link => link.removeAttribute('tabindex'));

    this.brandNavSlider.querySelectorAll('.filter-checkbox').forEach(cb => {
      cb.setAttribute('tabindex', '-1');
    });
  },

  /**
   * Horizontally reveals a tile without scrolling the document vertically.
   * @param {HTMLElement} tile
   */
  scrollNativeTileIntoView(tile) {
    const sliderRect = this.brandNavSlider.getBoundingClientRect();
    const tileRect = tile.getBoundingClientRect();
    const sliderStyles = window.getComputedStyle(this.brandNavSlider);
    const visibleLeft = sliderRect.left + parseFloat(sliderStyles.paddingLeft);
    const visibleRight =
      sliderRect.right - parseFloat(sliderStyles.paddingRight);
    let scrollDelta = 0;

    if (tileRect.left < visibleLeft) {
      scrollDelta = tileRect.left - visibleLeft;
    } else if (tileRect.right > visibleRight) {
      scrollDelta = tileRect.right - visibleRight;
    }

    if (scrollDelta !== 0) {
      this.brandNavSlider.scrollTo({
        left: this.brandNavSlider.scrollLeft + scrollDelta,
        behavior: 'smooth'
      });
    }
  },

  /**
   * Sets up all event listeners for brand navigation
   */
  setupEventListeners() {
    this.setupBrandNavInputListeners();
    this.setupFilterTileKeyboardListeners();
    this.setupFacetFilterListeners();
    this.setupClearAllFiltersListeners();
    this.setupBrandPillRemovalListeners();
  },

  /**
   * Adds keyboard listeners to filter tile labels so Space/Enter toggles the filter
   */
  setupFilterTileKeyboardListeners() {
    this.filterTiles.forEach(tile => {
      tile.addEventListener('keydown', e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          const checkbox = tile.querySelector('.filter-checkbox');
          if (checkbox) {
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });
  },

  /**
   * Adds change event listeners to brand navigation inputs
   * Toggles selected state and manages corresponding facet checkbox
   */
  setupBrandNavInputListeners() {
    this.brandNavInputs.forEach(input => {
      input.addEventListener('change', e => {
        const tile = e.target.closest('.js-filter-tile');
        const tileInputValue = e.target.value;

        const matchingFacet = Array.from(
          document.querySelectorAll(
            '.refinement.brand .ais-RefinementList-checkbox'
          )
        ).find(checkbox => checkbox.value === tileInputValue);

        this.setTileSelectedState(tile, e.target.checked);

        if (matchingFacet) {
          matchingFacet.click();
        }
      });
    });
  },

  /**
   * Sets up listeners for facet filter changes
   * Synchronizes brand tile selection with facet checkbox state
   */
  setupFacetFilterListeners() {
    document.addEventListener('change', event => {
      if (
        event.target.matches(
          '.refinement.brand input.ais-RefinementList-checkbox'
        )
      ) {
        const input = event.target;
        const inputValue = input.value;

        if (input.checked) {
          document.querySelectorAll('.filter-checkbox').forEach(checkbox => {
            if (checkbox.value === inputValue) {
              checkbox.checked = true;
              this.setTileSelectedState(
                checkbox.closest('.js-filter-tile'),
                true
              );
            }
          });
          // Keep the selected tile visible in native-scroll mode.
          if (this.brandNavSlider.classList.contains('is-native-scroll')) {
            this.goToFirstSelectedSlide();
          }
        } else {
          document.querySelectorAll('.filter-checkbox').forEach(checkbox => {
            if (checkbox.value === inputValue) {
              this.setTileSelectedState(
                checkbox.closest('.js-filter-tile'),
                false
              );
            }
          });
        }
      }
    });
  },

  /**
   * Sets up listener for clear all filters action
   * Removes selected state from all filter tiles
   */
  setupClearAllFiltersListeners() {
    document.addEventListener('click', event => {
      if (
        event.target.closest(
          'facet-remove[data-label="clear-all"], .js-clear-facets-btn'
        )
      ) {
        [...this.filterTiles].forEach(tile => {
          const checkbox = tile.querySelector('.filter-checkbox');
          if (checkbox) checkbox.checked = false;
          this.setTileSelectedState(tile, false);
        });
      }
    });
  },

  /**
   * Sets up listener for brand pill removal
   * Removes selected state from corresponding brand tile
   */
  setupBrandPillRemovalListeners() {
    document.addEventListener('click', event => {
      if (event.target.closest('facet-remove')) {
        const element = event.target.closest('facet-remove');
        const getFilterLabel = element.getAttribute('data-label');

        document.querySelectorAll('.filter-checkbox').forEach(checkbox => {
          if (checkbox.value === getFilterLabel) {
            this.setTileSelectedState(
              checkbox.closest('.js-filter-tile'),
              false
            );
          }
        });
      }
    });
  },

  /**
   * Retrieves URL parameters for a given parameter name
   * @param {string} name - The name of the URL parameter to retrieve
   * @returns {string[]} Array of parameter values
   */
  getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.getAll(name);
  },

  /**
   * Adds selected class to brand tiles based on URL parameters
   * Scrolls to the first selected slide
   */
  addSelectedClassOnLoad() {
    const vendorFilterValues = this.getUrlParameter('filter.p.vendor');

    if (vendorFilterValues.length) {
      this.brandNavInputs.forEach(checkbox => {
        if (vendorFilterValues.includes(checkbox.value)) {
          checkbox.checked = true;
          this.setTileSelectedState(checkbox.closest('.js-filter-tile'), true);
        }
      });
    }
    this.goToFirstSelectedSlide();
  },

  /**
   * Scrolls the slider to the first selected slide
   * Handles both initialized and initializing slider states
   */
  goToFirstSelectedSlide() {
    if (!this.brandNavSlider) return;

    const selectedTile = this.brandNavSlider.querySelector(
      'label.js-filter-tile.selected'
    );

    if (!selectedTile) return;

    const $slider = $(this.brandNavSlider);

    if (this.isSlickInitialized()) {
      const slick = $slider.slick('getSlick');
      const selectedSlide = selectedTile.closest('.slick-slide');
      const selectedSlideIndex = Number(
        selectedSlide?.getAttribute('data-slick-index')
      );

      if (Number.isNaN(selectedSlideIndex)) return;

      const firstVisibleSlide = slick.currentSlide;
      const lastVisibleSlide =
        firstVisibleSlide + slick.options.slidesToShow - 1;

      if (
        selectedSlideIndex < firstVisibleSlide ||
        selectedSlideIndex > lastVisibleSlide
      ) {
        // Measured rather than derived from slidesToShow: under variableWidth
        // the index arithmetic can land short of the true content edge.
        const lastStartingSlide = this.getLastStartingSlide();

        $slider.slick(
          'slickGoTo',
          Math.min(selectedSlideIndex, lastStartingSlide),
          true
        );
      }
    } else {
      this.scrollNativeTileIntoView(selectedTile);
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  BrandNavigation.init();
});
